// import bowser from 'bowser';
import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useCallback, useRef } from 'react';
import { useScrollTransition } from 'util/react-transition-component';
import PlayButton from 'components/atoms/play-button/PlayButton';
import Video from 'components/atoms/video/Video';
import styles from './HeaderShowreel.module.scss';
import { scrollOutTransition, setupTransitionInTimeline } from './HeaderShowreel.transitions';
import type { HeaderShowreelProps } from './HeaderShowreel.data';
import Heading from '../../atoms/heading/Heading';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import useFullScreen from './hooks/useFullScreen';
import { useIsDesktopDevice } from '../../../hooks/useIsDesktopDevice';
import { useGlobalStore } from '../../../stores/global';
// import useVideoSize from './hooks/useVideoSize';

export default function HeaderShowreel({
  canTransitionIn,
  ref,
  ...props
}: HeaderShowreelProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const title1Ref = useRef<HTMLHeadingElement>(null);
  const title2Ref = useRef<HTMLHeadingElement>(null);
  const title1MaskRef = useRef<HTMLHeadingElement>(null);
  const title2MaskRef = useRef<HTMLHeadingElement>(null);
  const maskedContainerRef = useRef<HTMLDivElement>(null);
  const shapeContainerRef = useRef<HTMLDivElement>(null);
  const shapePathRef = useRef<SVGPathElement>(null);
  const playButtonRef = useRef<HTMLButtonElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const maskedElementsRef = useRef<HTMLDivElement>(null);

  const keyPrefix = props.blok._uid;
  const clipId = `${keyPrefix}_mask`;

  const { enterFullScreen, exitFullScreen, isFullScreen } = useFullScreen(
    elementRef,
    title1MaskRef,
    title2MaskRef,
    playButtonRef,
    shapePathRef,
    shapeContainerRef,
    title1Ref,
    title2Ref,
    maskedElementsRef,
    clipId,
  );
  // useVideoSize(shapeContainerRef, videoWrapperRef, maskedContainerRef);

  const { inView } = useScrollTransition({
    scroll: {
      canTransitionIn,
      trigger: elementRef,
      vars: {},
      enableInView: true,
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        playButtonRef,
        title1Ref,
        title2Ref,
        title1MaskRef,
        title2MaskRef,
        maskedContainerRef,
        shapeContainerRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const store = useGlobalStore();
  const isDesktopDevice = useIsDesktopDevice();
  const onPlayClick = useCallback(() => {
    if (isDesktopDevice) {
      enterFullScreen();
    } else {
      store.openVideoModal({
        video: props.blok.video[0],
      });
    }
  }, [isDesktopDevice, store, props.blok.video, enterFullScreen]);

  useScrollTrigger({
    canTransitionIn,
    trigger: elementRef,
    animation: useSetupTimeline(scrollOutTransition, {
      title1MaskRef,
      title2MaskRef,
      title1Ref,
      title2Ref,
    }),
    vars: {
      scrub: true,
      start: 'top top',
      end: 'bottom top',
    },
  });

  return (
    <div
      ref={elementRef}
      className={classNames(styles.headerShowreel, props.className)}
      {...sbEditable(props.blok)}
    >
      <svg preserveAspectRatio="xMinYMin meet" className={classNames(styles.shape)}>
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            <path
              ref={shapePathRef}
              fill="white"
              d="M1026.74 213.242L721.248 518.735C606.51 403.997 420.489 403.997 305.751 518.735L0.257812 213.242C283.259 -69.7593 743.74 -69.7593 1026.74 213.242Z"
            />
          </clipPath>
        </defs>
      </svg>

      <div ref={shapeContainerRef} className={classNames(styles.shapeContainer, 'abs-center')}>
        <Heading
          as="h1"
          ref={title1Ref}
          size="heading-01"
          transitionInType="splitCharsSlideInUp"
          className={classNames(styles.title, styles.firstTitle)}
        >
          {props.blok.title_line1}
        </Heading>
        <Heading
          as="h1"
          ref={title2Ref}
          size="heading-01"
          transitionInType="splitCharsSlideInDown"
          className={classNames(styles.title, styles.secondTitle)}
        >
          {props.blok.title_line2}
        </Heading>
      </div>

      <div
        ref={maskedElementsRef}
        style={{
          clipPath: `url(#${clipId})`,

          WebkitClipPath: `url(#${clipId})`,
        }}
        className={classNames(styles.maskedElements, 'abs-fill')}
      >
        <div ref={maskedContainerRef} className={classNames(styles.maskedWrapper, 'abs-fill')}>
          <div className={classNames(styles.videoContainer, 'abs-center')}>
            <div ref={videoWrapperRef} className={classNames(styles.videoWrapper, 'abs-center')}>
              <Video
                className={classNames(styles.video, 'abs-fill')}
                blok={props.blok.video[0]}
                playing={inView}
                playsinline
                controls
                removeOnPause={false}
                openInModal={false}
                innerTransition={{
                  type: 'scaleUp',
                }}
                controlsVisible={isFullScreen}
                loop
                isFullScreen={isFullScreen} // isFullScreen && bowser.mobile
                muted={!isFullScreen}
                exitFullScreen={exitFullScreen}
              />
            </div>
          </div>

          <div className={classNames(styles.shapeContainer, 'abs-center')}>
            <PlayButton
              ref={playButtonRef}
              className={classNames(styles.playButton, 'abs-center')}
              isPlaying={isFullScreen}
              onClick={onPlayClick}
            />

            <Heading
              as="h1"
              size="heading-01"
              ref={title1MaskRef}
              transitionInType="none"
              className={classNames(styles.title, styles.firstTitle)}
            >
              {props.blok.title_line1}
            </Heading>
            <Heading
              as="h1"
              size="heading-01"
              ref={title2MaskRef}
              transitionInType="none"
              className={classNames(styles.title, styles.secondTitle)}
            >
              {props.blok.title_line2}
            </Heading>
          </div>
        </div>
      </div>
    </div>
  );
}
